import * as React from "react"
import { Check, ChevronDown, Search } from "lucide-react"
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import * as Portal from "@radix-ui/react-portal"

/**
 * GroupOrderCombobox - Searchable autocomplete for selecting parent/group orders
 * 
 * Features:
 * - Real-time search across all groups (including archived)
 * - Default view shows only recent groups (last 120 days)
 * - Rich display format: [Group Name] — [Customer Name] — [Date]
 * - Keyboard navigation (arrow keys, enter to confirm)
 * - Handles large datasets efficiently
 */
export function GroupOrderCombobox({
  options = [],
  value,
  onValueChange,
  onSearch,
  placeholder = "Search group orders...",
  emptyText = "No group orders found.",
  className,
  disabled = false,
  loading = false,
}) {
  const [open, setOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState("")
  const [highlightedIndex, setHighlightedIndex] = React.useState(0)
  const [dropdownPosition, setDropdownPosition] = React.useState({ top: 0, left: 0, width: 0 })
  
  const inputRef = React.useRef(null)
  const containerRef = React.useRef(null)
  const dropdownRef = React.useRef(null)
  const searchTimeoutRef = React.useRef(null)

  // Get display value for selected option
  const selectedOption = options.find(opt => opt.value === value)
  const displayValue = selectedOption ? formatOptionDisplay(selectedOption) : ""

  // Update dropdown position when opened
  React.useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      })
    }
  }, [open])

  // Handle clicks outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        open &&
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false)
        setSearchValue("")
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  // Debounced search
  React.useEffect(() => {
    if (open && searchValue && onSearch) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
      
      searchTimeoutRef.current = setTimeout(() => {
        onSearch(searchValue)
      }, 300) // 300ms debounce
    }
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchValue, open, onSearch])

  // Reset highlighted index when options change
  React.useEffect(() => {
    setHighlightedIndex(0)
  }, [options])

  // Scroll highlighted option into view
  React.useEffect(() => {
    if (open && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.querySelector(`[data-index="${highlightedIndex}"]`)
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    }
  }, [highlightedIndex, open])

  const handleSelect = (selectedValue) => {
    onValueChange(selectedValue)
    setOpen(false)
    setSearchValue("")
    inputRef.current?.blur()
  }

  const handleInputChange = (e) => {
    const newValue = e.target.value
    setSearchValue(newValue)
    
    if (!open) {
      setOpen(true)
    }
  }

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault()
        setOpen(true)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex((prev) => 
          prev < options.length - 1 ? prev + 1 : prev
        )
        break
      
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex((prev) => prev > 0 ? prev - 1 : prev)
        break
      
      case 'Enter':
        e.preventDefault()
        if (options[highlightedIndex]) {
          handleSelect(options[highlightedIndex].value)
        }
        break
      
      case 'Escape':
        e.preventDefault()
        setOpen(false)
        setSearchValue("")
        inputRef.current?.blur()
        break
      
      default:
        break
    }
  }

  const handleFocus = () => {
    setOpen(true)
  }

  const handleClear = () => {
    onValueChange('')
    setSearchValue("")
    inputRef.current?.focus()
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          value={open ? searchValue : displayValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cn("h-10 bg-secondary/50 pl-9 pr-8", className)}
        />
        <ChevronDown 
          className={cn(
            "absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none transition-transform",
            open && "rotate-180"
          )}
        />
      </div>
      
      {open && (
        <Portal.Root>
          <div 
            ref={dropdownRef}
            style={{
              position: 'absolute',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              zIndex: 9999
            }}
            className="mt-1 bg-popover border rounded-md shadow-lg max-h-[300px] overflow-auto"
            onMouseDown={(e) => e.preventDefault()}
          >
            {loading ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                Loading...
              </div>
            ) : options.length > 0 ? (
              <>
                {/* Clear selection option */}
                {value && (
                  <div
                    onClick={handleClear}
                    className="cursor-pointer hover:bg-secondary px-3 py-2 border-b text-sm text-muted-foreground"
                  >
                    Clear selection
                  </div>
                )}
                
                {/* Options list */}
                {options.map((option, index) => (
                  <div
                    key={option.value}
                    data-index={index}
                    onClick={() => handleSelect(option.value)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={cn(
                      "cursor-pointer px-3 py-2.5 flex items-start gap-2 transition-colors",
                      highlightedIndex === index && "bg-primary/10",
                      value === option.value && "bg-primary/20"
                    )}
                  >
                    <Check
                      className={cn(
                        "mt-0.5 h-4 w-4 shrink-0",
                        value === option.value ? "opacity-100 text-primary" : "opacity-0"
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">
                        {option.label}
                      </div>
                      {option.metadata && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {option.metadata}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                {emptyText}
              </div>
            )}
          </div>
        </Portal.Root>
      )}
    </div>
  )
}

/**
 * Format option display for rich metadata
 * @param {Object} option - Option object with label and metadata
 * @returns {string} Formatted display string
 */
function formatOptionDisplay(option) {
  if (option.metadata) {
    return `${option.label} — ${option.metadata}`
  }
  return option.label
}
